/**
 * Admin API Schemas
 *
 * Schemas for platform admin operations.
 */

import { z } from 'zod/v4';
import { searchTruncatedToSchema } from './common';

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Add credits request body - admin operation to adjust organization balance
 *
 * Currency support:
 * - `USD` - US Dollars (default)
 * - `EUR` - Euros
 * - `RUB` - Russian Rubles
 *
 * Source types (for audit trail):
 * - `adjustment` - Manual admin correction (e.g., billing error fix)
 * - `invoice` - Payment received against invoice
 * - `promo` - Promotional credit grant
 * - `prepaid` - Prepaid package purchase
 */
export const addCreditsBodySchema = z.object({
  amount: z.number().positive(), // Credit amount (can be positive or negative for adjustments)
  currency: z.enum(['USD', 'EUR', 'RUB']).default('USD'),
  source: z.enum(['adjustment', 'invoice', 'promo', 'prepaid']),
  description: z.string().min(1).max(500).optional(), // Admin notes (required for transparency)
  sourceReference: z.string().max(255).optional(), // External reference (invoice ID, promo code, etc.)
});

// NOTE: Use orgIdParamSchema from common.ts for organization ID params

// ============================================================================
// Query Schemas
// ============================================================================

export const adminPaginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(1000).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().optional(),
});

// ============================================================================
// Response Schemas
// ============================================================================

export const adminOrganizationResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string().nullable(),
  createdAt: z.string(),
  memberCount: z.number().int(),
  projectCount: z.number().int(),
  recordCount: z.number().int(),
  balance: z.number(),
});

export const adminOrganizationListResponseSchema = z.object({
  items: z.array(adminOrganizationResponseSchema),
  total: z.number().int(),
});

export const addCreditsResponseSchema = z.object({
  newBalance: z.number(),
  transactionId: z.string(),
});

// ============================================================================
// Global Credentials Schemas
// ============================================================================

/**
 * Global provider type categories
 *
 * - `asr` - Automatic Speech Recognition (ElevenLabs)
 * - `llm` - Large Language Models (OpenAI, Anthropic)
 * - `email` - Email delivery services (Resend)
 *
 * Used for organizing platform-wide fallback credentials in admin panel.
 * Organizations can override with their own credentials via organization_provider_credentials.
 */
export const globalProviderTypeSchema = z.enum(['asr', 'llm', 'email']);

export const providerCredentialsParamSchema = z.object({
  type: globalProviderTypeSchema,
  name: z.string(),
});

/**
 * Supported global credential provider types
 *
 * ASR providers:
 * - `elevenlabs` - ElevenLabs Scribe v2 (with speaker diarization)
 * - (removed 2026-09-11: `deepgram` — ElevenLabs is the only ASR sub-processor)
 *
 * LLM providers:
 * - `openai` - OpenAI GPT-5-mini (primary analysis LLM)
 * - (removed 2026-08-11: `openrouter`, `anthropic` — OpenAI is the only LLM sub-processor)
 *
 * Email providers:
 * - `resend` - Resend email API (transactional emails)
 *
 * Integration providers:
 * - `bitrix24` - Bitrix24 CRM (webhook authentication tokens)
 *
 * Platform admins configure these via POST /api/v1/admin/credentials/global
 */
export const globalCredentialTypeSchema = z.enum([
  // 'openrouter' and 'anthropic' were removed 2026-08-11: OpenAI is the only
  // LLM sub-processor on the published list. Retired names are refused on
  // WRITE (`isKnownProviderName` in edge-api's admin route) and ignored by
  // credential loading. Rows written before then are still LISTED by
  // `GET /admin/provider-credentials` — nothing filters them — so the
  // response shape they are validated against (`adminProviderCredentialSchema`)
  // deliberately keeps `providerName` a free string. Narrowing this enum is
  // therefore safe only because it never validates a stored row.
  'openai',
  'elevenlabs',
  'resend',
  'bitrix24',
]);

export const globalCredentialResponseSchema = z.object({
  id: z.string(),
  type: globalCredentialTypeSchema,
  name: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const listGlobalCredentialsResponseSchema = z.object({
  items: z.array(globalCredentialResponseSchema),
});

export const createGlobalCredentialRequestSchema = z
  .object({
    type: globalCredentialTypeSchema,
    name: z.string().min(1).max(100),
    apiKey: z.string().min(1),
    isActive: z.boolean().default(true),
  })
  .strict();

export const updateGlobalCredentialRequestSchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    apiKey: z.string().min(1).optional(),
    isActive: z.boolean().optional(),
  })
  .strict();

// Schema for global credentials endpoint
export const createGlobalCredentialBodySchema = z.object({
  providerType: globalProviderTypeSchema,
  providerName: z.string().min(1).max(100),
  credentialsJson: z.string().min(1),
  isActive: z.boolean().default(true),
});

/**
 * Why a feature sold from a tier up is closed: the plan is below its floor
 * (the answer is "upgrade") or the plan includes it and the flag is off (an
 * administrator's switch). Tier first: a lower plan is `tier_not_eligible`
 * even though its flag is off too, because that flag is off only as a
 * consequence of the tier default.
 */
export const tierFeatureRefusalSchema = z.enum(['tier_not_eligible', 'feature_disabled']);

/** One tier-gated feature's verdict for the organisation (#1231). */
export const featureGateVerdictSchema = z.union([
  z.object({ enabled: z.literal(true) }),
  z.object({ enabled: z.literal(false), reason: tierFeatureRefusalSchema }),
]);

/**
 * What a screen says about a Free organization's Pro trial (#2816, spec
 * docs/plans/2026-10-26-free-pro-trial.md, «Показ пробы»; `proTrialView` in
 * @aica/core): `available` — before it starts (the trial switched on, no
 * analysis yet), `active` — the banner with the days left to `endsAt`,
 * `ended` — the line on the lock (`endsAt` null: the stored key was unreadable,
 * the date is unknown). Absent or null — nothing to say: a paid organization,
 * or a Free one the trial is not open to.
 */
export const proTrialViewSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('available'), endsAt: z.null() }),
  z.object({ state: z.literal('active'), endsAt: z.string() }),
  z.object({ state: z.literal('ended'), endsAt: z.string().nullable() }),
]);

/**
 * GET /api/v1/feature-flags
 *
 * `degraded` is not decoration. When the flag store cannot be read the
 * endpoint still answers 200 with the platform defaults, because it is called
 * on every app load and failing it outright would turn a partial outage into a
 * blank UI. That fallback is indistinguishable from real data unless the
 * response says so — and an admin who cannot tell is an admin toggling flags
 * against a picture that is not their organisation's. Hence a REQUIRED field:
 * an optional one can be forgotten by both the server and the client, which is
 * the failure mode this exists to remove.
 */
export const featureFlagsResponseSchema = z.object({
  flags: z.record(z.string(), z.boolean()),
  /**
   * Every catalog feature sold from a tier up (`TIER_FEATURE_GATES` in
   * @aica/core), keyed by its flag: the boolean in `flags` cannot tell "plan
   * too low" from "switched off", and a screen behind such a feature must say
   * which (#1231). For these keys `flags[key]` is `gates[key].enabled` — one
   * truth in one answer. Empty when `degraded` — the store was not read, so there is
   * no verdict to give, and a client must treat it as "could not find out",
   * not as "no".
   */
  gates: z.record(z.string(), featureGateVerdictSchema),
  /**
   * The flags as stored and resolved, BEFORE the tier gate overwrote the
   * tier-gated keys — what the admin flag editor shows (switch position,
   * override mark). `flags` answers "may this organisation open it", which for
   * a Starter organisation with an explicit override is `false` while the
   * stored value is `true`; an editor reading `flags` would show the switch off
   * and hide the reset. Present only for a platform owner reading an
   * organisation by `targetOrgId`; an ordinary client never receives it.
   */
  rawFlags: z.record(z.string(), z.boolean()).optional(),
  orgId: z.string(),
  /** true → `flags` are platform defaults, NOT this organisation's stored flags. */
  degraded: z.boolean(),
  /**
   * The Pro trial to show (`proTrialViewSchema`). Null when there is nothing to
   * show and on a `degraded` answer — the organization was not read. Optional:
   * an edge-api older than the field sends nothing, which reads as null.
   */
  proTrial: proTrialViewSchema.nullable().optional(),
});

// ============================================================================
// Type exports
// ============================================================================

export type FeatureFlagsResponse = z.infer<typeof featureFlagsResponseSchema>;
export type FeatureGateVerdict = z.infer<typeof featureGateVerdictSchema>;
export type TierFeatureRefusal = z.infer<typeof tierFeatureRefusalSchema>;
export type ProTrialView = z.infer<typeof proTrialViewSchema>;
export type AddCreditsBody = z.infer<typeof addCreditsBodySchema>;
export type AdminOrganizationResponse = z.infer<typeof adminOrganizationResponseSchema>;
export type GlobalProviderType = z.infer<typeof globalProviderTypeSchema>;
export type GlobalCredentialType = z.infer<typeof globalCredentialTypeSchema>;
export type GlobalCredentialResponse = z.infer<typeof globalCredentialResponseSchema>;
export type CreateGlobalCredentialRequest = z.infer<
  typeof createGlobalCredentialRequestSchema
>;
export type UpdateGlobalCredentialRequest = z.infer<
  typeof updateGlobalCredentialRequestSchema
>;

// ============================================================================
// Frontend Response Schemas (for UI validation)
// ============================================================================

/**
 * Admin organization with stats (for admin panel list)
 */
export const adminOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string().optional(),
  region: z.string().nullable().optional(),
  industry: z.string().nullable().optional(),
  bitrix24Domain: z.string().nullable().optional(),
  /**
   * The org's active CRM portal domain — prefers the live embedded_installs
   * record over the deprecated `bitrix24Domain` column. Falls back to null
   * when the org never connected an embedded CRM (the UI then disambiguates
   * same-named orgs using the org ID instead).
   */
  portalDomain: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  membersCount: z.number().optional(),
  projectsCount: z.number().optional(),
  recordsCount: z.number().optional(),
  userCount: z.number().optional(),
  projectCount: z.number().optional(),
  balanceCents: z.number().optional(),
  /** Record count in the last 30 days (org health matrix, owner dashboard). */
  recordCount: z.number().int().optional(),
  /**
   * Budget usage percentage (spend / monthly limit * 100). Omitted when the
   * org has no budget limit configured — absence must NOT be read as 0%.
   */
  budgetUsagePercent: z.number().optional(),
});

/**
 * Organization credits with transactions
 */
export const adminOrgCreditsSchema = z.object({
  orgId: z.string(),
  balance: z.number(),
  currency: z.string(),
  transactions: z.array(
    z.object({
      id: z.string(),
      amount: z.number(),
      type: z.string(),
      description: z.string(),
      createdAt: z.string(),
    })
  ),
});

/**
 * Provider credential for admin panel.
 *
 * SECURITY: the secret payload (`credentialsJson`) is write-only — accepted on
 * create/update but NEVER serialized back to the client. "Configured" is
 * inferred from `hasCredentials`; to change a key the admin submits a fresh
 * value. See audit P0-2 (C1).
 */
export const adminProviderCredentialSchema = z.object({
  id: z.string(),
  providerType: z.string(),
  providerName: z.string(),
  hasCredentials: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * Failed delivery record (for health monitoring)
 *
 * Tracks delivery failures across integration channels.
 * Used by admin dashboard to identify delivery issues and trigger manual retries.
 *
 * Target types:
 * - `crm` - Bitrix24 field update failed
 * - `email` - Email notification delivery failed
 * - `webhook` - Outgoing webhook POST failed
 */
export const healthFailedDeliverySchema = z.object({
  id: z.string(),
  // The record and the target are absent for a narrowed access grant without
  // `records.read` (E1.5, #1336); every role gets both.
  recordId: z.string().optional(), // AICA record ID that failed to deliver
  projectId: z.string(),
  targetType: z.enum(['crm', 'email', 'webhook']),
  target: z.string().optional(), // CRM entity ID, email address, or webhook URL
  errorMessage: z.string().optional(), // HTTP error or exception message
  retryCount: z.number(), // Number of automatic retry attempts (max: 3)
  lastAttempt: z.string(), // ISO 8601 timestamp of last delivery attempt
  createdAt: z.string(),
});

/**
 * Integration health status - real-time monitoring of delivery channels
 *
 * Status values:
 * - `healthy` - Error rate < 5%, recent deliveries successful
 * - `degraded` - Error rate 5-20%, some failures but recoverable
 * - `down` - Error rate > 20% or all recent attempts failed
 *
 * Used in GET /api/v1/admin/health/integrations for ops dashboard.
 */
export const healthIntegrationSchema = z.object({
  type: z.enum(['crm', 'email', 'webhook']),
  provider: z.string().optional(), // 'bitrix24', 'resend', 'n8n', etc.
  status: z.enum(['healthy', 'degraded', 'down']),
  lastSuccess: z.string().optional(), // ISO 8601 timestamp
  lastFailure: z.string().optional(), // ISO 8601 timestamp
  errorRate: z.number().optional(), // Percentage (0-100)
  totalAttempts: z.number().optional(), // Last 24h delivery attempts
  failedAttempts: z.number().optional(), // Last 24h failures
});

// ============================================================================
// Admin Response Schemas
// ============================================================================

export const adminOrganizationsResponseSchema = z.object({
  success: z.boolean().optional(),
  organizations: z.array(adminOrganizationSchema),
  total: z.number().optional(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  /** Запрос пришлось укоротить под предел D1 — см. `searchTruncatedToSchema`. */
  searchTruncatedTo: searchTruncatedToSchema,
});

export const adminOrgCreditsResponseSchema = adminOrgCreditsSchema;

export const adminProviderCredentialsResponseSchema = z.object({
  data: z.array(adminProviderCredentialSchema),
  meta: z.object({
    total: z.number(),
  }),
});

export const adminProviderCredentialResponseSchema = z.object({
  data: adminProviderCredentialSchema,
});

/**
 * The deepest `offset` `GET /health/failed-deliveries` serves (#1580): with
 * more than one target type every source reads its first `offset + limit`
 * rows and the page is cut after the merge, so an unbounded offset is an
 * unbounded read per request. The server refuses a deeper offset (400); the
 * admin pager stops here instead of advertising a page it cannot load.
 */
export const FAILED_DELIVERIES_OFFSET_MAX = 10_000;

export const healthFailedDeliveriesResponseSchema = z.object({
  deliveries: z.array(healthFailedDeliverySchema).optional(),
  total: z.number().optional(),
});

export const healthIntegrationsResponseSchema = z.object({
  integrations: z.array(healthIntegrationSchema).optional(),
  lastCheck: z.string().optional(),
});

export const healthDeliveryRetryResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  deliveryId: z.string(),
  // The record and its run are absent for a narrowed access grant without
  // `records.read` (E1.5, #1336), as on the failed-deliveries list.
  recordId: z.string().optional(),
  workflowId: z.string().optional(),
  traceId: z.string().optional(),
});

// ============================================================================
// Frontend Type exports
// ============================================================================

export type AdminOrganization = z.infer<typeof adminOrganizationSchema>;
export type AdminOrgCredits = z.infer<typeof adminOrgCreditsSchema>;
export type AdminProviderCredential = z.infer<typeof adminProviderCredentialSchema>;
export type HealthFailedDelivery = z.infer<typeof healthFailedDeliverySchema>;
export type HealthIntegration = z.infer<typeof healthIntegrationSchema>;
export type AdminOrganizationsResponse = z.infer<typeof adminOrganizationsResponseSchema>;
export type AdminProviderCredentialsResponse = z.infer<
  typeof adminProviderCredentialsResponseSchema
>;
export type HealthFailedDeliveriesResponse = z.infer<
  typeof healthFailedDeliveriesResponseSchema
>;
export type HealthIntegrationsResponse = z.infer<typeof healthIntegrationsResponseSchema>;
export type HealthDeliveryRetryResponse = z.infer<
  typeof healthDeliveryRetryResponseSchema
>;
